/**
 * One assembly slot.
 *
 * Two sizes, and the size carries meaning rather than fitting a grid: `primary`
 * is a component the model's parts list requires, `compact` is one it does not.
 * An operator glancing at this under warehouse light should be able to answer
 * "can I confirm yet" without reading a word.
 *
 * Color is the state vocabulary the scanner already uses, kept deliberately:
 *   gray dashed  empty
 *   blue         scan target, the slot the operator is aiming at
 *   green        resolved in THIS session
 *   gray solid   already fitted before this session, shown but not re-committed
 *   amber        untracked, a board being created on the spot
 *   orange       resolved but the drone is still unknown
 *
 * Green versus gray is the distinction that matters at the bench: assembly is
 * incremental, so most sessions open with slots already full, and the operator
 * only has to get the NEW ones right.
 * These stay semantic rather than moving to the `primary` token, because
 * `primary` swaps per instance (#ffcc33 Firefly, #82b7ce Swarm) and the Swarm
 * value would collide with the blue scan-target state.
 */

/** An item kind key (`AP`, `ESC`, `PWR`, `RPI`...). The model's parts list decides which exist. */
export type SlotKey = string;

/** One slot as `GET /scanner/slots` returns it for a run or a model. */
export interface ModelSlot {
  key: SlotKey;
  label: string;
  source: 'board' | 'instance' | 'either';
  required: boolean;
  /** Kinds that fill the same parts-list line: a Kiwi's CM5 line is MOD or RPI, never both. */
  alternatives: SlotKey[];
}

export interface ScannedComponent {
  slot: SlotKey;
  /** Which table resolved it. Boards are flashed; instances carry a printed sticker. */
  source: 'board' | 'instance';
  /** What the commit endpoint is sent. */
  value: string;
  /** UID for a board, SKU for a part instance. */
  label: string;
  /** The drone this component is already fitted to, if any. */
  droneSerial: string | null;
  /** A board with no row yet, to be created inside the commit transaction. */
  untracked?: boolean;
  /** Already on this drone before this session. Shown, but not re-committed. */
  preexisting?: boolean;
}

interface Props {
  slot: SlotKey;
  label: string;
  component: ScannedComponent | null;
  size?: 'primary' | 'compact';
  scanTarget?: boolean;
  needsSerial?: boolean;
  onTap?: () => void;
}

// Single-path line icons for the kinds that have one, so a glance separates them
// by shape and not only by the word. A kind without one shows its key instead;
// this map only decorates, it never decides which slots exist.
const SLOT_ICON: Record<string, string> = {
  AP: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z',
  ESC: 'M13 10V3L4 14h7v7l9-11h-7z',
  LED: 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  PYR: 'M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z',
  GPS: 'M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z',
};

/** Break a hardware UID into 4-character groups so it can be read aloud. */
const formatUid = (uid: string) => (uid.match(/.{1,4}/g) || []).join('-');

const ComponentCard = ({
  slot, label, component, size = 'primary', scanTarget = false, needsSerial = false, onTap,
}: Props) => {
  const compact = size === 'compact';
  const icon = SLOT_ICON[slot];
  const box = compact
    ? 'rounded-lg p-2 min-h-[72px] gap-0.5'
    : 'rounded-xl p-4 min-h-[112px] gap-1';
  const iconSize = compact ? 'w-5 h-5' : 'w-7 h-7';
  const nameSize = compact ? 'text-[11px]' : 'text-sm';
  const base = `border-2 flex flex-col items-center justify-center text-center transition-colors ${box}`;

  if (!component) {
    return (
      <button
        type="button"
        onClick={onTap}
        aria-label={`${label}: not scanned. Tap to aim the scanner at this slot.`}
        className={`${base} w-full border-dashed focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${
          scanTarget
            ? 'border-blue-400 bg-blue-900/20'
            : 'border-gray-700 active:border-outline'
        }`}
      >
        {icon ? (
          <svg className={`${iconSize} ${scanTarget ? 'text-blue-400' : 'text-on-surface-disabled'}`}
               fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={icon} />
          </svg>
        ) : (
          <span aria-hidden="true" className={`font-mono font-bold ${compact ? 'text-xs' : 'text-sm'} ${scanTarget ? 'text-blue-400' : 'text-on-surface-disabled'}`}>
            {slot}
          </span>
        )}
        <span title={label} className={`${nameSize} max-w-full truncate font-medium tracking-wide ${scanTarget ? 'text-blue-300' : 'text-on-surface-variant'}`}>
          {label}
        </span>
        {!compact && (
          <span className={`text-xs ${scanTarget ? 'text-blue-400' : 'text-gray-700'}`}>
            {scanTarget ? 'Scan now' : 'Not scanned'}
          </span>
        )}
      </button>
    );
  }

  const tone = component.untracked
    ? { border: 'border-amber-500', bg: 'bg-amber-900/40', name: 'text-amber-400', body: 'text-amber-200' }
    // Already fitted before this session: present and correct, but not something
    // the operator just did. Muted so the eye goes to what IS new, which is the
    // only part they still have to get right.
    : component.preexisting
      ? { border: 'border-outline-variant', bg: 'bg-gray-800/40', name: 'text-on-surface-variant', body: 'text-on-surface-variant' }
      : needsSerial
      ? { border: 'border-orange-500', bg: 'bg-orange-900/50', name: 'text-orange-400', body: 'text-orange-200' }
      : { border: 'border-green-500', bg: 'bg-green-900/50', name: 'text-green-400', body: 'text-green-200' };

  return (
    <div className={`${base} ${tone.border} ${tone.bg}`}>
      <span title={label} className={`${nameSize} max-w-full truncate font-bold tracking-wide ${tone.name}`}>{label}</span>
      <span className={`${compact ? 'text-[10px]' : 'text-xs'} font-mono tabular-nums break-all ${tone.body}`}>
        {component.source === 'board' ? formatUid(component.label) : component.label}
      </span>
      {component.preexisting && (
        <span className="text-on-surface-variant text-[10px] tracking-wide">Already fitted</span>
      )}
      {component.untracked && (
        <span className="bg-amber-600/60 text-amber-100 text-[10px] font-bold px-2 py-0.5 rounded tracking-wide">
          New board
        </span>
      )}
      {needsSerial && !compact && (
        <span className="text-orange-300 text-xs">Scan the drone QR</span>
      )}
    </div>
  );
};

export default ComponentCard;
